import type { ReactNode } from "react";
import { Button } from "@client/components/Button";
import { useOnlineStatus } from "@client/hooks/useOnlineStatus";
import { describeError } from "@client/lib/errorMessage";

/**
 * The three states every query on a page can be in, given a shape so that a
 * loading list, a failed one and an empty one are never mistaken for each other.
 */

export function ErrorNotice({
  error,
  title = "That did not work",
  onRetry,
}: {
  error: unknown;
  title?: string;
  onRetry?: () => void;
}) {
  return (
    <div
      role="alert"
      className="flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3"
      style={{ borderColor: "var(--chart-crit)" }}
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <span
          className="text-xs font-bold uppercase tracking-wider"
          style={{ color: "var(--chart-crit)" }}
        >
          {title}
        </span>
        <span className="text-sm text-ink">{describeError(error)}</span>
      </div>
      {onRetry && (
        <Button variant="ghost" size="sm" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

/**
 * A skeleton rather than a spinner: it reserves the space the content will take,
 * so the page does not jump when it arrives. `aria-busy` and the visually hidden
 * label are what a screen reader gets instead of the shimmer.
 */
export function LoadingBlock({ rows = 3, label = "Loading" }: { rows?: number; label?: string }) {
  // React Query pauses a fetch rather than failing it when the browser is
  // offline, so the query stays pending and this skeleton would otherwise
  // shimmer for as long as the connection is down — the one loading state that
  // is never going to resolve. Say what is actually happening instead.
  const online = useOnlineStatus();
  if (!online) {
    return (
      <div
        role="status"
        className="rounded-lg border border-dashed border-line px-4 py-6 text-center text-sm text-ink-soft"
      >
        Waiting for a connection. This will load as soon as you are back online.
      </div>
    );
  }

  return (
    <div aria-busy="true" aria-live="polite" className="flex flex-col gap-2">
      <span className="sr-only">{label}</span>
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={index}
          className="h-12 animate-pulse rounded-lg border border-line bg-chip-bg"
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

/** A short, non-blocking confirmation — "Saved", "Shared with ada@dss.mv". */
export function SuccessNotice({ children }: { children: ReactNode }) {
  return (
    <p role="status" className="text-xs font-semibold" style={{ color: "var(--chart-ok)" }}>
      {children}
    </p>
  );
}
